/**
 * Story 8.12 — Onglet Présentation en lecture seule (modale profil membre)
 * Affiche les informations d'entreprise, description, CA et effectifs.
 * Aucun bouton d'édition. Aucune interaction.
 */
import React from 'react';
import type { ProfileData } from '../../../../../services/profileService';
import PrestataireLotMap from '../../../../../composants/PerimetreSelector/PrestataireLotMap';
import SectionPliable from '../../../../../composants/SectionPliable/SectionPliable';
import { adresseEntiteUneLigne } from '../../../../../utils/adresse';

interface ReadOnlyPresentationProps {
    data: ProfileData;
}

const formatSiret = (siret?: string | null): string => {
    if (!siret) return '-';
    return siret.replace(/(.{3})(.{3})(.{3})(.{5})/, '$1 $2 $3 $4');
};

const formatPhone = (phone?: string | null): string => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    let n = digits;
    if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
    else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
    else return phone;
    return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};

const formatEmail = (email?: string | null): string => email ? email.toLowerCase() : '-';

const formatAdresse = (adresse?: { numero_voie?: string | null; type_voie?: string | null; nom_voie?: string | null; code_postal?: string | null; ville?: string | null; pays?: string | null } | null): string => {
    if (!adresse) return '-';
    return adresseEntiteUneLigne(adresse) || '-';
};

const ReadOnlyPresentation: React.FC<ReadOnlyPresentationProps> = ({ data }) => {
    const { entreprise, presentation } = data;

    return (
        <div className="readonly-tab readonly-presentation">
            {/* Informations légales */}
            <SectionPliable title="Informations légales" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <div className="readonly-grid three-cols">
                        <div className="readonly-field">
                            <label>Nom de l'entreprise</label>
                            <p>{entreprise?.nom || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Précision</label>
                            <p>{entreprise?.precision || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>SIRET</label>
                            <p>{formatSiret(entreprise?.siret)}</p>
                        </div>
                    </div>
                    <div className="readonly-grid three-cols">
                        <div className="readonly-field">
                            <label>RCS</label>
                            <p>{entreprise?.RCS || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Statut juridique</label>
                            <p>{entreprise?.statut_juridique || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Capital</label>
                            <p>{entreprise?.capital ? `${entreprise.capital.toLocaleString('fr-FR')} €` : '-'}</p>
                        </div>
                    </div>
                    <div className="readonly-grid three-cols">
                        <div className="readonly-field">
                            <label>Année de création</label>
                            <p>{entreprise?.date_creation || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Redressement judiciaire</label>
                            <p>{entreprise?.redressement_judiciaire ? 'Oui' : 'Non'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Adresse</label>
                            <p>{formatAdresse(entreprise?.adresse)}</p>
                        </div>
                    </div>
                </div>
            </SectionPliable>

            {/* Représentant légal */}
            <SectionPliable title="Représentant légal" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <div className="readonly-grid three-cols">
                        <div className="readonly-field">
                            <label>Nom</label>
                            <p>{data.coordonnees?.legal_nom || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Prénom</label>
                            <p>{data.coordonnees?.legal_prenom || '-'}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Fonction</label>
                            <p>{data.coordonnees?.legal_fonction || '-'}</p>
                        </div>
                    </div>
                    <div className="readonly-grid three-cols">
                        <div className="readonly-field">
                            <label>Email</label>
                            <p>{formatEmail(data.coordonnees?.legal_email)}</p>
                        </div>
                        <div className="readonly-field">
                            <label>Téléphone</label>
                            <p>{formatPhone(data.coordonnees?.legal_tel)}</p>
                        </div>
                    </div>
                </div>
            </SectionPliable>

            {/* Périmètre géographique d'intervention : carte de France + select du lot technique. */}
            {data.lots && data.lots.lots_techniques && data.lots.lots_techniques.length > 0 && (
                <SectionPliable title="Périmètre géographique d'intervention" className="readonly-section-pliable">
                    <div className="readonly-section-content">
                        <PrestataireLotMap
                            lotsTechniques={data.lots.lots_techniques}
                            lotsDepartements={data.lots.lots_departements}
                        />
                    </div>
                </SectionPliable>
            )}

            {/* Description */}
            <SectionPliable title="Description de l'entreprise" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <div className="readonly-field">
                        <label>Présentation de l'entreprise</label>
                        <p className="readonly-text-block">{presentation?.presentation_entreprise || 'Non renseignée'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Présentation des moyens humains et matériel</label>
                        <p className="readonly-text-block">{presentation?.presentation_humain || 'Non renseignée'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Présentation des modes opératoires</label>
                        <p className="readonly-text-block">{presentation?.presentation_operationnel || 'Non renseignée'}</p>
                    </div>
                    <div className="readonly-field">
                        <label>Engagements environnementaux et sociaux</label>
                        <p className="readonly-text-block">{presentation?.presentation_environnement || 'Non renseignée'}</p>
                    </div>
                </div>
            </SectionPliable>

            {/* Capacités économiques */}
            <SectionPliable title="Capacités économiques et financières" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <p className="readonly-hint">Chiffre d'affaires global annuel en € et Part du CA des prestations similaires (%)</p>
                    <div className="readonly-grid-pairs">
                        <div className="readonly-pair">
                            <span className="pair-label">(N-1)</span>
                            <span className="pair-value">{presentation?.ca_n1?.toLocaleString('fr-FR') || '-'} €</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_ca_n1 ?? '-'} %</span>
                        </div>
                        <div className="readonly-pair">
                            <span className="pair-label">(N-2)</span>
                            <span className="pair-value">{presentation?.ca_n2?.toLocaleString('fr-FR') || '-'} €</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_ca_n2 ?? '-'} %</span>
                        </div>
                        <div className="readonly-pair">
                            <span className="pair-label">(N-3)</span>
                            <span className="pair-value">{presentation?.ca_n3?.toLocaleString('fr-FR') || '-'} €</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_ca_n3 ?? '-'} %</span>
                        </div>
                    </div>
                </div>
            </SectionPliable>

            {/* Capacités techniques */}
            <SectionPliable title="Capacités techniques et professionnelles" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <p className="readonly-hint">Effectifs moyens annuels et Part du personnel d'encadrement (%)</p>
                    <div className="readonly-grid-pairs">
                        <div className="readonly-pair">
                            <span className="pair-label">(N-1)</span>
                            <span className="pair-value">{presentation?.rh_n1 ?? '-'} ETP</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_rh_n1 ?? '-'} %</span>
                        </div>
                        <div className="readonly-pair">
                            <span className="pair-label">(N-2)</span>
                            <span className="pair-value">{presentation?.rh_n2 ?? '-'} ETP</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_rh_n2 ?? '-'} %</span>
                        </div>
                        <div className="readonly-pair">
                            <span className="pair-label">(N-3)</span>
                            <span className="pair-value">{presentation?.rh_n3 ?? '-'} ETP</span>
                            <span className="pair-label">Part</span>
                            <span className="pair-value">{presentation?.part_rh_n3 ?? '-'} %</span>
                        </div>
                    </div>
                </div>
            </SectionPliable>
        </div>
    );
};

export default ReadOnlyPresentation;
